import React from "react";
import { Loader2, Save } from "lucide-react";
import {
  daysSince,
  formatContactDate,
} from "@/pages/admin/contact/constants";

/**
 * Expanded detail panel for a single contact request — project description,
 * editable internal notes, plus metadata side panel.
 */
const ContactRowDetails = ({
  req,
  lang,
  notesDraft,
  setNotesDraft,
  savingNote,
  onSaveNote,
}) => (
  <tr className="bg-[#0a0a0a] border-b border-[#F2EBE5]/5">
    <td colSpan={6} className="px-4 py-6">
      <div className="grid md:grid-cols-2 gap-6 text-sm">
        <div className="space-y-5">
          <div>
            <p className="kw-label mb-2">
              {lang === "fr" ? "Détails du projet" : "Project details"}
            </p>
            <p className="text-[#F2EBE5] whitespace-pre-wrap leading-relaxed">
              {req.project_details}
            </p>
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="kw-label">
                {lang === "fr" ? "Notes internes" : "Internal notes"}
              </p>
              <button
                type="button"
                onClick={() => onSaveNote(req.id)}
                disabled={savingNote === req.id || notesDraft[req.id] === undefined}
                data-testid={`admin-notes-save-${req.id}`}
                className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] hover:text-[#F2EBE5] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {savingNote === req.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Save className="w-3 h-3" strokeWidth={1.5} />
                )}
                {lang === "fr" ? "Enregistrer" : "Save"}
              </button>
            </div>
            <textarea
              value={notesDraft[req.id] ?? req.internal_notes ?? ""}
              onChange={(e) =>
                setNotesDraft((d) => ({ ...d, [req.id]: e.target.value }))
              }
              data-testid={`admin-notes-${req.id}`}
              rows={4}
              placeholder={
                lang === "fr"
                  ? "Vos notes (prix négocié, prochaine relance, contexte client...)"
                  : "Your notes (negotiated price, next follow-up, client context...)"
              }
              className="w-full kw-input text-sm bg-[#050505] resize-none"
            />
          </div>
        </div>
        <div className="space-y-3 text-xs text-[#A19E9C]">
          {req.preferred_date && (
            <p>
              <span className="kw-label mr-2">
                {lang === "fr" ? "Date souhaitée" : "Preferred date"} :
              </span>
              <span className="text-[#F2EBE5]">{req.preferred_date}</span>
            </p>
          )}
          <p>
            <span className="kw-label mr-2">
              {lang === "fr" ? "Langue" : "Language"} :
            </span>
            <span className="text-[#F2EBE5] uppercase">{req.language}</span>
          </p>
          {req.status_changed_at && (
            <p>
              <span className="kw-label mr-2">
                {lang === "fr" ? "Statut depuis" : "Status since"} :
              </span>
              <span className="text-[#F2EBE5]">
                {formatContactDate(req.status_changed_at, lang)}
                {(() => {
                  const d = daysSince(req.status_changed_at);
                  return d !== null ? (
                    <span className="text-[#A19E9C] ml-2">
                      ({d} {lang === "fr" ? "j" : "d"})
                    </span>
                  ) : null;
                })()}
              </span>
            </p>
          )}
          {req.last_followup_sent_at && (
            <p>
              <span className="kw-label mr-2">
                {lang === "fr" ? "Dernière relance" : "Last follow-up"} :
              </span>
              <span className="text-[#E8C66C]">
                {formatContactDate(req.last_followup_sent_at, lang)}
              </span>
            </p>
          )}
          <p>
            <span className="kw-label mr-2">{lang === "fr" ? "Réf" : "Ref"} :</span>
            <span className="text-[#F2EBE5]/60 font-mono">
              #{req.id.slice(0, 8).toUpperCase()}
            </span>
          </p>
        </div>
      </div>
    </td>
  </tr>
);

export default ContactRowDetails;
